import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { fetchDataVersion } from "@/api/dataVersion";
import {
  POLL_MS,
  freshness,
  isOutdated,
  observeDataVersion,
  useDataVersion,
} from "@/lib/dataVersion";

import "./DataVersionStatus.css";

const LABEL = {
  current: "Actueel",
  updating: "Wordt bijgewerkt",
  unconfirmed: "Niet gecontroleerd of dit actueel is",
} as const;

/**
 * De markering uit plan 17 §7 (E4), in de kop van elk scherm.
 *
 * Wat er staat, is wat de cijfers op het scherm waard zijn:
 *
 * - **Actueel**: alles op het scherm hoort bij de laatst bevestigde versie, en
 *   die bevestiging is jonger dan 60 s;
 * - **Wordt bijgewerkt**: de API meldde een nieuwere versie en een deel van
 *   het scherm wordt nog opnieuw gevraagd;
 * - **Niet gecontroleerd of dit actueel is**: de laatste bevestiging is ouder
 *   dan 60 s (geen verbinding, of het tabblad stond op de achtergrond).
 *
 * De versie wordt elke 20 s nagevraagd zolang het tabblad zichtbaar is, en
 * meteen bij terugkomen. Kent de API geen dataversie (migratie 0050 ontbreekt),
 * dan toont dit niets: dan werkt de cache zoals voorheen, op tijd.
 */
export function DataVersionStatus() {
  const versie = useDataVersion();
  const client = useQueryClient();
  const [nu, setNu] = useState(() => Date.now());

  useQuery({
    queryKey: ["data-version"],
    queryFn: async () => {
      const antwoord = await fetchDataVersion();
      // Ook zonder kop (bijvoorbeeld een 304 die een proxy zonder koppen gaf)
      // telt de waarde in het antwoord als bevestiging.
      if (antwoord.data_version !== null) observeDataVersion(antwoord.data_version);
      return antwoord;
    },
    refetchInterval: POLL_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: "always",
    staleTime: 0,
  });

  useEffect(() => {
    const klok = window.setInterval(() => setNu(Date.now()), 2000);
    return () => window.clearInterval(klok);
  }, []);

  if (versie.latest === null) return null;

  const verouderd = client.getQueryCache().findAll({ type: "active" }).some((query) => isOutdated(query));
  const basis = freshness(versie, undefined, nu);
  const stand = basis === "current" && verouderd ? "updating" : basis;
  const bevestigd = versie.confirmedAt
    ? new Date(versie.confirmedAt).toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit", second: "2-digit" })
    : "nooit";

  return (
    <div
      className={`atl-dataversion atl-dataversion--${stand}`}
      role="status"
      aria-live="polite"
      title={`Dataversie ${versie.latest}, laatst bevestigd om ${bevestigd}`}
    >
      <span className="atl-dataversion__dot" aria-hidden="true" />
      <span>{LABEL[stand]}</span>
    </div>
  );
}
